import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PhoneInput from "react-phone-input-2";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { Alert, Spin } from "antd";
import useGlobalHandler from "../../hooks/useGlobalHandler";
import {
  createaAccountWithPhoneNumber,
  setUpRecaptcha,
  signUp,
} from "../../hooks/useSignup";
import { useTranslation } from "react-i18next";

const SignUpForm = () => {
  const { t } = useTranslation("preAuth");

  const signupSchema = Yup.object().shape({
    phoneNumber: Yup.string("")
      .min(12, t("phone number is too short"))
      .max(13, t("Phone Number is too long"))
      .required(),
    email: Yup.string().email("Invalid email").required(t("email is required")),
    password: Yup.string()
      .required(t("no password provided"))
      .min(8, "Password is too short - should be 8 characters minimum.")
      .matches(
        /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/,
        "Password must contain at least one letter and one number."
      ),
    confirmPassword: Yup.string()
      .required(t("please retype your password"))
      .oneOf([Yup.ref("password")], t("your passwords do not match")),
  });
  const [counter, setCounter] = useState(0);
  useEffect(() => {
    const timer =
      counter > 0 && setInterval(() => setCounter(counter - 1), 1000);
    return () => clearInterval(timer);
  }, [counter]);
  const [appVerifier, setAppVerifier] = useState();
  const formRef = useRef();
  const { isLoading, setIsLoading, isError, setIsError, error, setError } =
    useGlobalHandler();
  const [confirmObj, setConfirmObj] = useState();
  const [flag, setFlag] = useState(false);
  const [showPassword, setshowPassword] = useState(false);
  const [showConfirmPassword, setshowConfirmPassword] = useState(false);
  const [otpCode, setotpCode] = useState("");
  const resendOtpCode = async () => {
    const values = formRef.current.values;

    setCounter(30);
    const confirmObject = await createaAccountWithPhoneNumber(
      values.phoneNumber,
      appVerifier
    );
    setConfirmObj(confirmObject);
  };

  const verifyOTP = async () => {
    const values = formRef.current.values;
    setIsError(false);
    setIsLoading(true);
    setError("");
    if (otpCode === "" || otpCode === null) {
      setIsLoading(false);
      setIsError(true);
      setError("please provide valid number");
    } else {
      try {
        await signUp(
          otpCode,
          confirmObj,
          values.email.toLowerCase().trim(),
          values.password
        );
      } catch (error) {
        setIsError(true);
        switch (error.code) {
          case "auth/invalid-credential":
            setError("Invalid Credential");
            break;
          case "auth/network-request-failed":
            setError("Network Error, Please Try Again");
            break;
          case "auth/email-already-in-use":
            setError(
              "Email already used, if you already have an account try login"
            );
            break;
          case "auth/provider-already-linked":
            break;
          default:
            setError(error.code);
            break;
        }
        setIsLoading(false);
      }
    }
  };

  return (
    <Formik
      innerRef={formRef}
      initialValues={{
        email: "",
        password: "",
        confirmPassword: "",
        phoneNumber: "",
        otpCode: "",
      }}
      validationSchema={signupSchema}
      onSubmit={async (values) => {
        try {
          setIsError(false);
          setError("");
          setIsLoading(true);
          const res = await setUpRecaptcha(values.phoneNumber, values.email);

          setCounter(30);
          setAppVerifier(res);
          const confirmObject = await createaAccountWithPhoneNumber(
            values.phoneNumber,
            res
          );
          setConfirmObj(confirmObject);
          setFlag(true);
          setIsLoading(false);
        } catch (error) {
          setIsError(true);
          switch (error.code) {
            case "auth/invalid-credential":
              setError("Invalid Credential");
              break;
            case "auth/network-request-failed":
              setError("Network Error, Please Try Again");
              break;
            case "auth/email-already-in-use":
              setError(
                "Email already used, if you already have an account try login"
              );
              break;
            case "auth/phone-already-in-use":
              setError(
                "Phone Number already used, if you already have an account try login"
              );
              break;
            case "auth/provider-already-linked":
              break;
            default:
              setError(error.code);
              break;
          }
          setIsLoading(false);
        }
      }}
    >
      {({ errors, touched, setFieldValue, field }) => (
        <div className="bg-white py-8 px-8 rounded-lg md:w-1/3 min-w-[94%] md:min-w-max">
          <Form className="flex flex-col gap-4 ">
            <h1 className="LoginTtitle">{t("sign up")}</h1>
            {!flag ? (
              <>
                <div className="flex flex-col gap-4">
                  <span className="flex flex-col gap-2">
                    <label className="text-primaryColor">
                      {t("email")}
                      <span className="text-sm font-normal text-gray-500 max-w-80">
                        {"( البريد الإلكتروني)"}
                      </span>{" "}
                    </label>
                    <Field
                      className="border-2 rounded-md p-1"
                      name="email"
                      type="email"
                      placeholder={t("email")}
                    />
                  </span>
                  <span className="flex flex-col gap-2">
                    <label className="text-primaryColor">
                      {t("phone number")}{" "}
                      <span className="text-sm font-normal text-gray-500 max-w-80">
                        {"(رقم الهاتف)"}
                      </span>
                    </label>
                    <PhoneInput
                      name="phoneNumber"
                      id="phoneNumber"
                      autoFormat={true}
                      disableCountryCode={true}
                      inputStyle={{ width: `100%` }}
                      country={"dz"}
                      {...field}
                      onChange={(phone, country) => {
                        const reducedPhone = phone.replace(
                          country.dialCode,
                          ""
                        );
                        setFieldValue(
                          "phoneNumber",
                          `+${country.dialCode}${reducedPhone.replace(
                            /^0+/,
                            ""
                          )}`
                        );
                      }}
                    />
                  </span>
                  <span className="flex flex-col gap-2 relative  ">
                    <label className="text-primaryColor ">
                      {t("password")}{" "}
                      <span className="text-sm font-normal text-gray-500 max-w-80">
                        {"( كلمة المرور)"}
                      </span>{" "}
                    </label>
                    <Field
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder={t("password")}
                      className="border-2 rounded-md p-1"
                    />
                    <EyeOutlined
                      className={`absolute top-[63%] right-2 ${
                        showPassword ? "" : "hidden"
                      }`}
                      onClick={() => setshowPassword(!showPassword)}
                    />
                    <EyeInvisibleOutlined
                      className={`absolute top-[63%] right-2 ${
                        !showPassword ? "" : "hidden"
                      }`}
                      onClick={() => setshowPassword(!showPassword)}
                    />
                  </span>
                  <span className="flex flex-col gap-2 relative  ">
                    <label className="text-primaryColor">
                      {t("confirm password")}{" "}
                      <span className="text-sm font-normal text-gray-500 max-w-80">
                        {"(تأكيد كلمة المرور)"}
                      </span>
                    </label>
                    <Field
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder={t("confirm password")}
                      className="border-2 rounded-md p-1"
                    />
                    <EyeOutlined
                      className={`absolute top-[63%] right-2 ${
                        showConfirmPassword ? "" : "hidden"
                      }`}
                      onClick={() =>
                        setshowConfirmPassword(!showConfirmPassword)
                      }
                    />
                    <EyeInvisibleOutlined
                      className={`absolute top-[63%] right-2 ${
                        !showConfirmPassword ? "" : "hidden"
                      }`}
                      onClick={() =>
                        setshowConfirmPassword(!showConfirmPassword)
                      }
                    />
                  </span>
                  {isError && <Alert message={error} type="error" showIcon />}
                  {errors.password && touched.password ? (
                    <Alert message={errors.password} type="error" showIcon />
                  ) : null}
                  {errors.email && touched.email ? (
                    <Alert message={errors.email} type="error" showIcon />
                  ) : null}
                  {errors.phoneNumber && touched.phoneNumber ? (
                    <Alert message={errors.phoneNumber} type="error" showIcon />
                  ) : null}
                  {errors.confirmPassword && touched.confirmPassword ? (
                    <Alert
                      message={errors.confirmPassword}
                      type="error"
                      showIcon
                    />
                  ) : null}
                </div>
                <div className="flex flex-col gap-2">
                  <motion.button
                    id="sign-in-button"
                    whileTap={{ scale: 0.9 }}
                    whileHover={{ scale: 1.05 }}
                    className="primaryLoginButton"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? <Spin /> : t("send confirmation code")}
                  </motion.button>
                </div>
                <p className="self-center">
                  {t("already have an account? login")}{" "}
                  <Link
                    to="/auth/login"
                    className="underline underline-offset-1 text-primaryColor"
                  >
                    {t("here")}
                  </Link>
                </p>
              </>
            ) : (
              <>
                <span className="flex flex-col gap-2">
                  {" "}
                  <label className="text-primaryColor">
                    {t("confirmation code")}
                  </label>
                  <input
                    name="otpCode"
                    type="number"
                    className="border-2 rounded-md p-1"
                    placeholder={t("confirmation code")}
                    onChange={(e) => setotpCode(e.target.value)}
                  />
                </span>
                <div className="flex flex-col gap-2">
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    whileHover={{ scale: 1.05 }}
                    className="primaryLoginButton"
                    type="button"
                    onClick={() => verifyOTP()}
                    disabled={isLoading}
                  >
                    {isLoading ? <Spin /> : "Confirm"}
                  </motion.button>
                </div>
                <span className="self-center">
                  Didn't recieve Confirmation Code ?{" "}
                  {counter === 0 ? (
                    <span
                      to="/auth/login"
                      className="underline underline-offset-1 text-primaryColor cursor-pointer"
                      onClick={() => resendOtpCode()}
                    >
                      Resend Code
                    </span>
                  ) : (
                    <span
                      to="/auth/login"
                      className="underline underline-offset-1 text-primaryColor"
                    >
                      Resend Code In {counter} s
                    </span>
                  )}
                </span>
                {errors.otpCode && touched.otpCode ? (
                  <Alert message={errors.otpCode} type="error" showIcon />
                ) : null}
              </>
            )}
          </Form>
        </div>
      )}
    </Formik>
  );
};

export default SignUpForm;
